import { getISOWeek, parse } from 'date-fns'
import {
  StockProjectionExceptionTableData,
  WeekRangeConfig,
} from './StockProjectionException.types'
import _ from 'lodash'

export const getFormattedGraphData = (
  stockProjectionExceptionGraphData:
    | StockProjectionExceptionTableData[]
    | StockProjectionExceptionTableData
    | null,
  weekRangeConfig: WeekRangeConfig,
  selectedGraphs: string[]
) => {
  if (!stockProjectionExceptionGraphData) {
    return undefined
  }

  let rows = []
  if (!Array.isArray(stockProjectionExceptionGraphData)) {
    // Casting to an array incase data is an object.
    rows = [{ ...stockProjectionExceptionGraphData }]
  } else {
    rows = [
      ...(stockProjectionExceptionGraphData as StockProjectionExceptionTableData[]),
    ]
  }

  const selectedGraphsSet = new Set(selectedGraphs)
  rows = rows.filter((row) => selectedGraphsSet.has(row.replenishmentSite))

  const startWeek = weekRangeConfig.start ? weekRangeConfig.start : 0
  const endWeek = weekRangeConfig.end ? weekRangeConfig.end : 50000000
  // Group by replenishment site.
  const formatedDataBySite = _.groupBy(rows, (row) => row.replenishmentSite)

  const weeksCopy = new Set(
    rows
      .filter(
        (row: StockProjectionExceptionTableData) =>
          row.dateKey >= startWeek && row.dateKey <= endWeek
      )
      .map(
        (row: StockProjectionExceptionTableData) =>
          row.stockProjectionExceptionWeek
      )
  )
  const weeks = []
  for (const week of weeksCopy) {
    weeks.push(week, week, week)
  }

  const dateKeys = []
  const dateKeyCopy = new Set(
    rows
      .filter(
        (row: StockProjectionExceptionTableData) =>
          row.dateKey >= startWeek && row.dateKey <= endWeek
      )
      .map((row: StockProjectionExceptionTableData) => {
        const date = parse(row.dateKey.toString(), 'yyyyMMdd', new Date())
        const isoWeekNumber = getISOWeek(date)
        // Get the year. year is added because isoweek is repeating after 52 weeks.
        const year = date.getFullYear()
        return `${isoWeekNumber}_${year}`
      })
  )

  for (const dateKey of dateKeyCopy) {
    dateKeys.push(dateKey, dateKey, dateKey)
  }

  const plotData = []
  let y_max = Infinity * -1
  let y_min = Infinity
  const sites = []
  for (const replenishmentSite in formatedDataBySite) {
    sites.push(replenishmentSite)
    const weeklyDetails = formatedDataBySite[replenishmentSite]
    const detailLength = weeklyDetails.length
    const { weekStartInventory: base } = weeklyDetails[0]
    const name = `${replenishmentSite}`
    const safetystockVolumes: number[] = []
    const exceptions: number[] = []
    const toolTipTexts: string[] = []
    const FD: number[] = []
    const SITA: number[] = []
    const PVA: number[] = []
    for (let i = 0; i < detailLength; i++) {
      const {
        stockinTransitArrival,
        safetyStockVolume,
        replenishmentPlanArrival,
        forecastDemandSignal,
        stockProjectionExceptionWeek,
        weekStartInventory,
        minimumInventory,
        dateKey,
        productionDateFormatted,
      } = weeklyDetails[i]

      if (dateKey >= startWeek && dateKey <= endWeek) {
        const text =
          `<b>Week:  </b>${stockProjectionExceptionWeek}<br>` +
          ` <b>Start of the week inventory: </b>${(+weekStartInventory).toFixed(
            2
          )}<br>` +
          ` <b>Forecast demand volume: </b>${(+forecastDemandSignal).toFixed(
            2
          )}<br>` +
          ` <b>Stock in transit arrival: </b>${(+stockinTransitArrival).toFixed(
            2
          )}<br>` +
          ` <b>Production volume arrival: </b>${(+replenishmentPlanArrival).toFixed(
            2
          )}<br>` +
          ` <b>Stock policy: </b>${(+safetyStockVolume).toFixed(2)}<br>` +
          ` <b>Minimum week inventory: </b>${(+minimumInventory).toFixed(
            2
          )}<br>` +
          ` <b>Week of production: </b>${productionDateFormatted}<br>`

        toolTipTexts.push(text, text, text)
        FD.push(Number(forecastDemandSignal.toFixed(2)), 0, 0)
        SITA.push(0, stockinTransitArrival, 0)
        PVA.push(0, 0, replenishmentPlanArrival)
        exceptions.push(
          Number((forecastDemandSignal * -1).toFixed(2)),
          Number(stockinTransitArrival.toFixed(2)),
          Number(replenishmentPlanArrival.toFixed(2))
        )
        safetystockVolumes.push(
          safetyStockVolume,
          safetyStockVolume,
          safetyStockVolume
        )
      }
    }
    const FD_MASK: number[] = []
    const SITA_MASK: number[] = []
    const PVA_MASK: number[] = []
    exceptions.reduce(
      (previousValue: number, currentValue: number, currentIndex: number) => {
        const value = Number((previousValue + currentValue).toFixed(2))
        y_max = Math.max(value, y_max, safetystockVolumes[currentIndex])
        y_min = Math.min(y_min, value, safetystockVolumes[currentIndex])
        FD_MASK.push(FD[currentIndex])
        FD_MASK[currentIndex] = FD[currentIndex] !== 0 ? value : 0
        SITA_MASK.push(SITA[currentIndex])
        SITA_MASK[currentIndex] = SITA[currentIndex] !== 0 ? previousValue : 0
        PVA_MASK.push(PVA[currentIndex])
        PVA_MASK[currentIndex] = PVA[currentIndex] !== 0 ? previousValue : 0
        return value
      },
      base
    )

    plotData.push({
      base,
      name,
      weeks,
      exceptions,
      safetystockVolumes,
      toolTipTexts,
      FD,
      SITA,
      PVA,
      FD_MASK,
      SITA_MASK,
      PVA_MASK,
      ISOWeeks: dateKeys,
    })
  }
  return { plotData, y_max, y_min, sites }
}
